/**
 * escapeClaimTarget — shared target marker for inline click-to-edit inputs
 * hosted inside an ancestor Radix dismissable surface (Popover/Dialog) that
 * must claim Escape for themselves (cancel the in-progress edit) instead of
 * letting it bubble into the ancestor's dismiss.
 *
 * Why this exists (F1 — Escape-in-popover seam consolidation): Radix's
 * `DismissableLayer` closes its content on Escape via a keydown listener
 * registered at DOCUMENT-level CAPTURE, which always runs before any
 * bubble-phase React handler on a descendant input — so an inline editor's
 * own `onKeyDown={(e) => e.preventDefault()}` is already too late to stop the
 * dismiss. `DismissableLayer` exposes exactly one supported extension point
 * for this: the content's own `onEscapeKeyDown` prop, invoked on the
 * highest-layer content BEFORE its `!event.defaultPrevented` dismiss guard
 * runs. An ancestor wires ONE `onEscapeKeyDown` that checks whether the
 * event's `target` is inside a claimed editor and `preventDefault()`s when it
 * is — replacing what used to be a per-input `window`-capture workaround
 * duplicated across every editor.
 *
 * Usage:
 *   - The editor spreads `escapeClaimProps` onto the input (or a container
 *     ancestor of every input it owns) that should own Escape while mounted
 *     — e.g. only rendered while the editor's own `editing` state is true.
 *   - The ancestor dismissable surface's content calls
 *     `onEscapeKeyDown={(e) => { if (isEscapeClaimTarget(e.target)) e.preventDefault(); }}`.
 *   - Elsewhere (no such ancestor listener), the attribute is inert — safe to
 *     apply unconditionally in a component shared across hosts.
 *
 * First consumer: `CompactVqePopover` (VQE-hosted inline editors —
 * `FilterEditablePill`, `LimitValueInline`, and `FilterChip.tsx`'s
 * `DraftFilterChip` reused via `VqeFilterChipBuilder`). Deliberately
 * host-agnostic so any future ancestor-dismissable-surface can reuse it.
 */
export const ESCAPE_CLAIM_ATTR = 'data-escape-claim';

/** Spread onto the input/container that should own Escape while mounted. */
export const escapeClaimProps: Record<string, string> = {
  [ESCAPE_CLAIM_ATTR]: 'true',
};

/** Called by the ancestor's `onEscapeKeyDown` with the raw event's `target`. */
export function isEscapeClaimTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(`[${ESCAPE_CLAIM_ATTR}]`) !== null;
}
